import { cx } from '@emotion/css'
import React, { CSSProperties } from 'react'
import { CssStyles } from '../../types'
import { getClassName, getCSSSize, parseClassNames } from '../../utils'

interface IImage {
  /**
   * 	Specifies the path to the image
   */
  src?: string

  /**
   * 	Identifies one or more image candidate strings, each specifying image resources to use under given circumstances
   */
  srcSet?: string

  /**
   * 	Specifies an alternate text for the image
   */
  alt?: string

  /**
   * Specifies the width of the image
   */
  width?: string | number

  /**
   * Specifies the height of the image
   */
  height?: string | number

  /**
   * Allows you to specify the layout width of the image for each of a list of media conditions.
   */
  sizes?: string

  /** *Deprecated* Raw CSS styles
   * @deprecated
   * */
  styles?: CssStyles<'root'>

  /**
   * Additional information displayed as a tooltip when the image is hovered.
   */
  title?: string

  /**
   * Specifies whether a browser should load the image immediately or to defer loading of images until some conditions are met
   * @default eager
   */
  loading?: 'eager' | 'lazy'

  /**
   * Represents a hint given to the browser on how it should decode the image.
   */
  decoding?: 'async' | 'auto' | 'sync'

  /**
   * Specifies the Cross-Origin Resource Sharing (CORS) setting to use when retrieving the image.
   */
  crossOrigin?: 'anonymous' | 'use-credentials' | ''

  /**
   * The ARIA role attribute of the element.
   */
  role?: string

  /** Provide an optional function to be called when the image is clicked */
  onClick?: () => void

  style?: CSSProperties | string
}

export const Image = React.forwardRef<HTMLImageElement, IImage>(
  (
    {
      src,
      srcSet,
      alt,
      width,
      height,
      sizes,
      styles,
      title,
      role,
      loading = 'eager',
      decoding = 'auto',
      crossOrigin = '',
      onClick,
      style,
    },
    ref
  ) => {
    const classNames = styles && parseClassNames(styles)

    return (
      <img
        ref={ref}
        src={src}
        srcSet={srcSet}
        alt={alt}
        width={getCSSSize(width)}
        height={getCSSSize(height)}
        sizes={sizes}
        className={cx(classNames?.root, getClassName(style))}
        loading={loading}
        title={title}
        onClick={onClick}
        {...(decoding ? { decoding } : {})}
        {...(crossOrigin ? { crossOrigin } : {})}
        {...(role ? { role } : {})}
      />
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  Image.displayName = 'Image'
}
